import { Flex, LoadingIndicator } from "@jsq/ds/core";
import { FormSelect, FormTextInput, useFormContext } from "@jsq/ds/forms";
import { SPACING } from "~/styles/variables";
import type { BillingGroupFormValues } from "./types";
import type {
  GetSafeCaseIdsForClientQuery,
  SearchFundFamiliesQuery,
} from "~/graphql/supergraphTypes";

type FundFamilySummary = NonNullable<
  SearchFundFamiliesQuery["searchFundFamilies"]
>["families"][number];

type SafeCaseIdOption =
  GetSafeCaseIdsForClientQuery["getSafeCaseIdsForClient"]["safeCaseIdOptions"][number];

export type ConditionalFieldsProps = {
  families: FundFamilySummary[];
  loading: boolean;
  safeCaseIdOptions: SafeCaseIdOption[];
  safeCaseIdsLoading: boolean;
  isSafeCaseIdEnabled: boolean;
};

export const ConditionalFields = ({
  families,
  loading,
  safeCaseIdOptions,
  safeCaseIdsLoading,
  isSafeCaseIdEnabled,
}: ConditionalFieldsProps) => {
  const { values } = useFormContext<BillingGroupFormValues>();

  const safeCaseIdSelector = isSafeCaseIdEnabled ? (
    safeCaseIdsLoading ? (
      <LoadingIndicator variant="primary" />
    ) : (
      <FormSelect
        name="safeCaseId"
        label="Safe case id"
        placeholder="Select safe case id..."
        data-testid="safe-case-id-select"
        full
      >
        <FormSelect.Section>
          {safeCaseIdOptions.map((option) => (
            <FormSelect.Option key={option.value} value={option.value}>
              {option.label}
            </FormSelect.Option>
          ))}
        </FormSelect.Section>
      </FormSelect>
    )
  ) : null;

  if (values.assignmentType === "existing") {
    if (loading) {
      return <LoadingIndicator variant="primary" />;
    }

    const fundFamilySelect = (
      <FormSelect
        name="fundFamily"
        label="Fund family"
        placeholder="Select fund family..."
        data-testid="fund-family-select"
        full
      >
        <FormSelect.Section>
          {families.map((family) => (
            <FormSelect.Option key={family.id} value={family.id}>
              {family.displayName || family.canonicalName}
            </FormSelect.Option>
          ))}
        </FormSelect.Section>
      </FormSelect>
    );

    if (!safeCaseIdSelector) {
      return fundFamilySelect;
    }

    return (
      <Flex flexDirection="column" gap={SPACING.MEDIUM}>
        {safeCaseIdSelector}
        {fundFamilySelect}
      </Flex>
    );
  }

  const fundFamilyNameInput = (
    <FormTextInput
      name="fundFamilyName"
      label="Fund family name"
      data-testid="fund-family-name-input"
      isFullWidth
    />
  );

  if (!safeCaseIdSelector) {
    return fundFamilyNameInput;
  }

  return (
    <Flex flexDirection="column" gap={SPACING.MEDIUM}>
      {safeCaseIdSelector}
      {fundFamilyNameInput}
    </Flex>
  );
};
